'use client'

import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react'

interface AssessmentHeaderContextType {
  showBackButton: boolean
  onBackClick: () => void
  isPending: boolean
  setHeaderState: (state: { showBackButton: boolean; onBackClick: () => void; isPending: boolean }) => void
  logoHref: string
  setLogoHref: (href: string) => void
}

export const DEFAULT_ASSESSMENT_LOGO_HREF = 'https://www.lifehack.org'

const AssessmentHeaderContext = createContext<AssessmentHeaderContextType | undefined>(undefined)

export function AssessmentHeaderProvider({ children }: { children: ReactNode }) {
  const [headerState, setHeaderState] = useState({
    showBackButton: false,
    onBackClick: () => {},
    isPending: false
  })
  const [logoHref, setLogoHref] = useState(DEFAULT_ASSESSMENT_LOGO_HREF)

  const memoizedSetHeaderState = useCallback((state: { showBackButton: boolean; onBackClick: () => void; isPending: boolean }) => {
    setHeaderState(state)
  }, [])

  const contextValue = useMemo(() => ({
    ...headerState,
    setHeaderState: memoizedSetHeaderState,
    logoHref,
    setLogoHref
  }), [headerState, logoHref, memoizedSetHeaderState])

  return (
    <AssessmentHeaderContext.Provider value={contextValue}>
      {children}
    </AssessmentHeaderContext.Provider>
  )
}

export function useAssessmentHeaderContext() {
  const context = useContext(AssessmentHeaderContext)
  if (!context) {
    throw new Error('useAssessmentHeaderContext must be used within AssessmentHeaderProvider')
  }
  return context
}
